import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore'; 
import KanbanBoard from '../components/crm/KanbanBoard';
import { Plus, LayoutGrid, List, Settings } from 'lucide-react'; 
import ApplicantDetailModal from '../components/crm/ApplicantDetailModal';
import ApplicantTable from '../components/crm/ApplicantTable';
import ManageRolesModal from '../components/crm/ManageRolesModal'; // IMPORT NEW MODAL

const HiringView = ({ currentUserProfile }) => {
    const [applicants, setApplicants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // --- SETTINGS STATE ---
    const [availableRoles, setAvailableRoles] = useState([]); // List of job titles
    const [roleFilter, setRoleFilter] = useState('All'); // Current filter selection

    // --- UI STATE ---
    const [viewMode, setViewMode] = useState('board');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    // --- MODAL STATE ---
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false); // For managing roles

    const HIRING_STATUSES = ['Applied', 'Interviewing', 'Offer Sent', 'Hired', 'Rejected'];
    const HIRING_COLORS = {
        'Applied': 'bg-blue-50',
        'Interviewing': 'bg-yellow-50',
        'Offer Sent': 'bg-purple-50',
        'Hired': 'bg-green-50',
        'Rejected': 'bg-gray-200'
    };

    // 1. Fetch Applicants AND Roles
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;

        // Listener for Applicants
        const qApplicants = query(collection(db, `organizations/${currentUserProfile.organizationId}/applicants`));
        const unsubApplicants = onSnapshot(qApplicants, (snapshot) => {
            setApplicants(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });

        // Fetch Roles (Single fetch or listener - let's use a function to fetch initially)
        const fetchRoles = async () => {
            const settingsRef = doc(db, `organizations/${currentUserProfile.organizationId}/settings`, 'hiring');
            const snap = await getDoc(settingsRef);
            if (snap.exists() && snap.data().roles) {
                setAvailableRoles(snap.data().roles);
            }
        };
        fetchRoles();

        return () => unsubApplicants();
    }, [currentUserProfile]);

    // 2. Filter & Sort Logic
    const filteredAndSortedApplicants = useMemo(() => {
        let filtered = [...applicants];

        // Apply Role Filter
        if (roleFilter !== 'All') {
            filtered = filtered.filter(app => app.role === roleFilter);
        }

        // Apply Sort
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let valA = a[sortConfig.key] || '';
                let valB = b[sortConfig.key] || '';
                if (sortConfig.key === 'createdAt') {
                    valA = a.createdAt?.toDate?.() || new Date(0);
                    valB = b.createdAt?.toDate?.() || new Date(0);
                }
                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return filtered;
    }, [applicants, sortConfig, roleFilter]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleStatusChange = async (docId, newStatus) => {
        const docRef = doc(db, `organizations/${currentUserProfile.organizationId}/applicants`, docId);
        await updateDoc(docRef, { status: newStatus });
    };

    const handleAddApplicant = () => {
        setSelectedApplicant({});
        setIsDetailModalOpen(true);
    };

    const handleOpenModal = (applicant) => {
        setSelectedApplicant(applicant);
        setIsDetailModalOpen(true);
    };

    const handleDeleteApplicant = async (applicant) => {
        if (window.confirm(`Are you sure you want to remove ${applicant.name}?`)) {
             try {
                await deleteDoc(doc(db, `organizations/${currentUserProfile.organizationId}/applicants`, applicant.id));
            } catch (error) {
                console.error("Error deleting applicant:", error);
            }
        }
    }

    if (isLoading) return <div className="p-6">Loading Pipeline...</div>;

    return (
        <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold">Hiring Pipeline</h1>
                    
                    {/* ROLE FILTER DROPDOWN */}
                    <select 
                        value={roleFilter} 
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="bg-white border border-gray-300 text-gray-700 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block p-2"
                    >
                        <option value="All">All Roles</option>
                        {availableRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </select>

                    {/* MANAGE ROLES BUTTON */}
                    <button 
                        onClick={() => setIsSettingsModalOpen(true)}
                        className="text-gray-500 hover:text-blue-600 p-1"
                        title="Manage Job Roles"
                    >
                        <Settings size={20} />
                    </button>
                </div>
                
                <div className="flex items-center gap-2">
                    <div className="flex bg-gray-100 p-1 rounded-md border border-gray-200">
                        <button 
                            onClick={() => setViewMode('board')} 
                            className={`p-2 rounded-md transition-all ${viewMode === 'board' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <LayoutGrid size={20} />
                        </button>
                        <button 
                            onClick={() => setViewMode('table')} 
                            className={`p-2 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <List size={20} />
                        </button>
                    </div>

                    <button 
                        onClick={handleAddApplicant}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-medium"
                    >
                        <Plus size={16} /> Add Applicant
                    </button>
                </div>
            </div>

            {/* PASSING FILTERED DATA TO BOARD/TABLE */}
            {viewMode === 'board' ? (
                <KanbanBoard 
                    items={filteredAndSortedApplicants} 
                    statuses={HIRING_STATUSES}
                    statusColors={HIRING_COLORS}
                    onStatusChange={handleStatusChange}
                    openModal={handleOpenModal} 
                    currentUserProfile={currentUserProfile} 
                />
            ) : (
                <ApplicantTable 
                    applicants={filteredAndSortedApplicants}
                    openModal={handleOpenModal}
                    requestSort={requestSort}
                    sortConfig={sortConfig}
                    onDelete={handleDeleteApplicant}
                />
            )}

            {/* DETAIL MODAL - NOW RECEIVES ROLES */}
            <ApplicantDetailModal 
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                applicant={selectedApplicant}
                currentUserProfile={currentUserProfile}
                availableRoles={availableRoles} 
            />

            {/* NEW SETTINGS MODAL */}
            <ManageRolesModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                currentUserProfile={currentUserProfile}
                currentRoles={availableRoles}
                onRolesUpdated={setAvailableRoles}
            />
        </div>
    );
};

export default HiringView;